/* global document, _hmt, dataLayer */
import { Modal } from 'antd-mobile'
import { getCookie } from './cookie'
import dayjs from 'dayjs'
import areaDataSc from '../../assets/pca-code-m2.json'
import config from '../../services/config.json'

export const isiOS = () => new RegExp('\\biPhone\\b|\\biPod\\b', 'i').test(window.navigator.userAgent)

export const isEmpty = (obj) => {
  for (let i in obj) {
    return false
  }
  return true
}

export const formatMoney = (amount, currency, withAbbr = false) => {
  const symbol = currency
  return withAbbr ? currency + ' ' + Number(amount).toFixed(2) : symbol + ' ' + Number(amount).toFixed(2)
}

/**
 * 「实收与应收不符、已交人工核对」的订单：后端 PaymentMethodType.ERROR = 0（CHNA-2755）。
 * <p>
 * 这类单后端刻意留在 WAIT_PAY（不自动关单、不重复入账），但重付被 getPaymentUrl 拒绝、
 * 关单被 closeOrder 拒绝 —— 只看 status === 'WAIT_PAY' 就放行支付按钮，用户点了必然报错、
 * 取消也关不掉。识别它才能显示「核对中」而不是给一个死按钮。
 * <p>
 * 教材订单列表/详情 DTO 一直下发 paymentMethod；字段缺失或为 null（非常规商品等复用同一页面的
 * 订单、历史数据）都判定为 false —— 注意不能写成 Number(paymentMethod) === 0：
 * Number(null) 与 Number('') 都是 0，会把字段缺失的单误判成核对中、连支付按钮一起藏掉。
 */
export const PAYMENT_METHOD_ERROR = 0

export const isOrderUnderAmountReview = (order) => order?.paymentMethod === PAYMENT_METHOD_ERROR

/**
 * 「这张卡面对该证书可装备」= 已拥有 ∪ VIP 当前可用。与后端 `EcardImageService#isFaceAvailable`
 * 同一口径，卡面列表/详情页据此决定按钮是「点击装备」还是「点击解锁」。
 * <p>
 * ⚠️ `ecardImageBindingId` **不是**可以清理的遗留字段：后端 `EcardImageBinding#toDTO()` 照旧下发它，
 * 它是列表里「已拥有」的唯一信号（`vipAvailable` 只表示「此刻 VIP 可用」，不含所有权——普通购买/
 * 活动/赠图的脸 `vipAvailable` 为 false）。把它从判断里删掉，已拥有的普通卡面会掉进「取消装备」分支，
 * 把「装备」变成「取消装备」（2026-10 排查过一次同样的误判）。
 * <p>
 * ⚠️ 只适用于 `GET /api/ecards/images`（`config.allCardsImg`）返回的卡面对象：`vipAvailable` **仅由该接口
 * 赋值**。`EcardImageBinding#toDTO()` 直出的对象——证书列表 `cert.ecardImages`——该字段恒为 `false`，
 * 对这类对象调用本函数，VIP 目录面会被误判成不可装备（按钮从「装备」退回「解锁/取消」）。
 * 后台订单详情的 `issuedCardFaces` 已改用后台自己的 VO，**响应里根本没有 `vipAvailable` 等三个 C 端标志**，
 * 不适用本函数也不适用上面这条误判（2026-10-04 后端改动，见后端 `shared/self/breaking-changes.md`）。
 * 证书列表侧的「当前正装备」要另看 `active`（装备位现算，见后端 `EcardService#findCardImageBindings`）。
 */
export const isFaceEquippable = (card) => !!(card?.ecardImageBindingId || card?.vipAvailable)

/**
 * VIP「换面集」面 id（与后端 `EcardImageCatalog.VIP_CHANGE_BASE` 同口径：40/47/51/52/53）。
 * <p>
 * 这些面：① 不落所有权（无 `ecardImageBindingId`）、② 不在 VIP 可用集里（连 VIP 用户的 `vipAvailable`
 * 也恒 false），因此 {@link isFaceEquippable} 对它们恒 false。它们**唯一**的装备入口是
 * `POST /api/ecardImage/binding`（载荷字段 `ecardImgId`；后端校验 level > 0 后写装备位）；发
 * `PUT /api/ecardImage/rebinding` 会被可得性判据（已拥有 ∪ VIP 可用集）**静默跳过**——接口 200、
 * 前端却弹「成功」（见 shared/self/api-contract.md）。
 * <p>
 * ⚠️ 不能只按「无绑定行 + `vipAvailable=false`」反推换面集：宽集 `GET /api/ecards/images` 里
 * 「碧海之蓝」（购买面 74）未拥有时同样没有绑定行、`vipAvailable=false`，但它**不能**走自助绑定
 * （后端 `requireSelfServiceBinding` 抛 `ecard.image.bind.not.allowed`），也不该显示装备按钮——它要买。
 * 故按 id 精确判定，与后端目录同源。
 */
export const VIP_CHANGE_FACE_IDS = [40, 47, 51, 52, 53]

/** 该面是否属 VIP「换面集」（按 id 判，与后端目录同源）。 */
export const isVipChangeFace = (face) => face != null && VIP_CHANGE_FACE_IDS.includes(face.ecardImageId)

/**
 * 「无绑定行的换面集面」——只能走 `POST /api/ecardImage/binding` 装备（`PUT /rebinding` 会被静默跳过）。
 * 已拥有（有绑定行）的换面集面不在此列：后端 `equip` 的「已拥有」分支能正常装备它。
 */
export const isSelfServiceBindFace = (face) => isVipChangeFace(face) && !face.ecardImageBindingId

/**
 * 回读权威状态后判「期望的展示位是否已生效」——换面成功后决定要不要提示「更换成功」用。
 * <p>
 * `PUT /api/ecardImage/rebinding` 只回显请求（后端对不可得面静默跳过），响应**不代表**真的装备上了；
 * `POST /api/ecardImage/binding` 也可能被权益校验拒掉。故一律回读 `GET /api/ecards/images` /
 * `GET /api/ecards` 后按下面的口径判：
 * <ul>
 *   <li>期望装备目录面（`ecardImageId`）→ 该面 `active`；</li>
 *   <li>期望装备自定义面（`bindingId`）→ 该自定义面 `active`；</li>
 *   <li>期望普通版（两者都不传）→ 没有任何面 `active`。</li>
 * </ul>
 * 形参 `container` 是宽集响应本身，或证书列表里的单张证书（都有 `ecardImages` / `customizedImages`）。
 */
export const isFaceSelectionApplied = (container, { ecardImageId, bindingId } = {}) => {
  if (!container) return false
  const faces = container.ecardImages || []
  const customized = container.customizedImages || []
  if (ecardImageId != null) return faces.some((face) => face.ecardImageId === ecardImageId && face.active)
  if (bindingId != null) return customized.some((item) => item.id === bindingId && item.active)
  return !faces.some((face) => face.active) && !customized.some((item) => item.active)
}

/**
 * 证书详情页「选中某个卡面」→ 决定装备端点与载荷（CHNA-2765）。
 * <p>
 * 卡面展示位两个真源：目录面写 `ecard_image_equipped`（后端 `EcardImageService#doEquip`），
 * 自定义面写 `ecard_customization.active`。证书列表 `cert.ecardImages` 是**窄集**（已拥有 ∪ 当前装备），
 * 且其中 `vipAvailable` **恒为 false**（只有 `GET /api/ecards/images` 会赋值）——拿窄集判「可装备」，
 * 会把「VIP 期内、无绑定行、被切走后 active=false」的面误判成不可装备，于是把「装备」发成「取消装备」：
 * 既装不回去，还会卸掉当前装备的面。故判据要取宽集里同 `ecardImageId` 的权威面
 * （{@link isFaceEquippable} ∨ `active`）。
 * <p>
 * 端点约定：普通版（无面）走 `PUT /api/ecardImage/cancelBinding`（只读 ecardId，卸掉当前装备位）；
 * 目录面 / 自定义面走 `PUT /api/ecardImage/rebinding`（按 `ecardImageId` / `bindingId`）；
 * 无绑定行的换面集面走 `POST /api/ecardImage/binding`（按 `ecardImgId`，见 {@link isSelfServiceBindFace}）。
 *
 * @param {object} p
 * @param {string} p.ecardId 证书 id（affiliateCertificationId || affiliateCredentialId）
 * @param {object} [p.face] 窄集里的目录面（含 ecardImageId）
 * @param {object} [p.authoritativeFace] 宽集里同 ecardImageId 的权威面（有 vipAvailable）
 * @param {number|string} [p.customBindingId] 自定义面 id
 * @param {boolean} [p.plastic] 是否「普通版」（无面）
 * @returns {{endpoint: 'rebinding'|'cancelBinding'|'binding', payload: Array}|null}
 *   返回 `null` 表示不发请求（既不该 rebinding，也不该 cancelBinding）
 */
export const resolveEcardFaceSelection = ({ ecardId, face, authoritativeFace, customBindingId, plastic }) => {
  if (plastic) return { endpoint: 'cancelBinding', payload: [{ ecardId }] }
  if (customBindingId != null) return { endpoint: 'rebinding', payload: [{ ecardId, bindingId: customBindingId }] }
  const target = authoritativeFace || face
  if (!target) return null
  // 换面集面（无绑定行）：rebinding 会被后端静默跳过，`active` 也不代表能走 rebinding——从 40 切到其他面
  // 再切回时，未刷新的宽集仍可能带 active=true。唯一入口是 POST /binding；是否真有权益由后端判
  // （非 VIP 回 club.vip.need，让它报错、不要假成功）。
  if (isSelfServiceBindFace(target)) {
    return { endpoint: 'binding', payload: [buildEcardImgBindItem(ecardId, target.ecardImageId)] }
  }
  // 可得 = 已拥有 ∨ VIP 当前可用 ∨ 当前装备（装备位现算，见后端 EcardService#ecardImages）
  if (!(isFaceEquippable(target) || !!target.active)) return null
  return { endpoint: 'rebinding', payload: [{ ecardId, ecardImageId: target.ecardImageId }] }
}

/**
 * `POST /api/ecardImage/binding` 的单条载荷：字段名是 **`ecardImgId`**，不是 `rebinding` 的 `ecardImageId`。
 * 抄错不会报错——后端 Jackson 忽略未知字段，只会静默不生效（2026-10 修过一次「权益页换面没生效」）。
 * 三个自助绑定入口（ClubEcards / NewYearCampaign / ClubEcardsEquity）共用本函数，字段名只此一处，改
 * 字段名必须同步后端契约（`shared/self/api-contract.md`）。
 */
export const buildEcardImgBindItem = (ecardId, ecardImgId) => ({ ecardId, ecardImgId })

export const groupBy = (original = [], groupKey) => {
  const result = {}
  original.forEach((o) => {
    if (result[o[groupKey]]) {
      result[o[groupKey]].push(o)
    } else {
      result[o[groupKey]] = [o]
    }
  })
  return result
}
export const getEncodedId = (ecard) => ecard.affiliateCertificationId || ecard.affiliateCredentialId

export const showError = (message, onPress = () => {}) => {
  Modal.alert('错误', message, [{ text: '关闭', onPress }])
}

export const getDivingHistory = (certs) => {
  return certs.length ? certs[certs.length - 1].issueDate.split('-')[2] : ''
}

export const gtm = (eventAction, eventLabel, value) => {
  dataLayer.push({
    event: 'interaction',
    eventCategory: 'Navigation',
    eventAction: eventAction,
    eventLabel: eventLabel,
    eventValue: value,
  })
}

// U Meng Push
const UPush = (action, options) => {
  let actionMap = {
    click: 'aplus.record',
    pv: 'aplus.sendPV',
    setting: 'aplus.setMetaInfo',
  }

  if (actionMap[action]) {
    const { aplus_queue } = window
    aplus_queue.push({
      action: actionMap[action],
      arguments: [...options],
    })
  }
}
//友盟点击事件
export const UClick = (eventCode, eventParams = {}) => {
  let option = [
    eventCode,
    'CLK', //点击事件  第二个参数固定CLK
    eventParams, //参数必须是一个json对象
  ]
  UPush('click', option)
}
export const debug = (...args) => {
  console.log('>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>')
  console.log(...args)
  console.log('<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<')
}

export const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1)

/**
 * 是否**确实**运行在微信内置浏览器里（只看 UA，不看 cookie）。
 * <p>
 * 与 {@link isWechat} 的区别很关键：`isWechat()` 还把「cookie 恰好 28 位」也算作微信环境——
 * 那表达的是「当前登录态是微信 openid/unionid」（取 openid、走微信授权要用它），
 * **不能用来决定「登录成功后要不要把新 token 写进 cookie」**：
 * 桌面浏览器里残留一个 28 位旧 token 时 `isWechat()` 为真，邮箱登录会「按微信规则」保留旧
 * openid、静默丢掉本次登录拿到的新 token，于是列表请求继续带旧 token → 后端一直回
 * `expired.token`（「登录过期」），重复登录也出不来（见 emailLogin 的登录成功分支）。
 */
export const isWechatBrowser = () => window.navigator.userAgent.toLocaleLowerCase().indexOf('micromessenger') > 0

export const isWechat = () => isWechatBrowser() || Boolean(getCookie('padiWxId') && getCookie('padiWxId').length === 28)

export const courseName = {
  'Enriched Air Diver': 'EnrichedAirDiver',
  'EFR - Primary Care (CPR) & Sec Care (1st) w/ AED': 'EFR',
  'AWARE Coral Reef Conservation Specialty': 'CoralReefConservation',
  'Equipment Specialist': 'EquipmentSpecialist',
  'Advanced Open Water': 'AdvancedOpenWater',
  'Open Water': 'OpenWater',
  'Peak Performance Buoyancy': 'PeakPerformanceBuoyancy',
  Drift: 'DriftDiver',
  'Rescue Diver': 'RescueDiver',
  Divemaster: 'DiveMaster',
  'Advanced Freediver': 'AdvancedFreediver',
  'Master Freediver': 'MasterFreediver',
  Freediver: 'Freediver',
  'Basic Freediver': 'BasicFreediver',
  'PADI Discover Mermaid': 'DiscoverMermaid',
  'PADI Basic Mermaid': 'BasicMermaid',
  'PADI Mermaid': 'Mermaid',
  'PADI Advanced Mermaid': 'AdvancedMermaid',
}

export const getCurrentGoods = (cardOption, data, certName) => {
  const currentGoods = cardOption[data.ecardId].cardStockDTOS.find((option) => {
    return option.skuId === data.skuId
  })
  currentGoods.ecardId = data.ecardId
  currentGoods.ecardName = certName
  return currentGoods
}

export const InputOnBlur = () => {
  setTimeout(() => {
    window.scrollBy(0, 1)
    window.scrollBy(0, -1)
  }, 100)
}

export const region_sc = [
  {
    label: '大陆+86',
    value: '86',
  },
  {
    label: '香港+852',
    value: '852',
  },
  {
    label: '台湾+886',
    value: '886',
  },
  {
    label: '澳门+853',
    value: '853',
  },
]

export const region_tc = [
  {
    label: '大陸+86',
    value: '86',
  },
  {
    label: '香港+852',
    value: '852',
  },
  {
    label: '台灣+886',
    value: '886',
  },
  {
    label: '澳門+853',
    value: '853',
  },
]

export const region_en = [
  {
    label: 'Mainland+86',
    value: '86',
  },
  {
    label: 'HongKong+852',
    value: '852',
  },
  {
    label: 'Taiwan+886',
    value: '886',
  },
  {
    label: 'Macao+853',
    value: '853',
  },
]

export const isMobile = (value) => {
  return /^[0-9]{7,15}$/.test(value)
}

export const isCode = (value) => {
  return /^\d{6}$/.test(value)
}

// 大陆手机号：1 开头 11 位，第二位 3-9（13x~19x 在售号段；10x/11x/12x 未分配）。
// 与后端 EcardSaleService / TextbookSaleService 的 MAINLAND_MOBILE_PATTERN 同口径
export const isMainlandMobile = (value) => {
  return /^1[3-9]\d{9}$/.test(value)
}

export const isNumber = (val) => {
  const regPos = /^\d+(\.\d+)?$/
  const regNeg = /^(-(([0-9]+\.[0-9]*[1-9][0-9]*)|([0-9]*[1-9][0-9]*\.[0-9]+)|([0-9]*[1-9][0-9]*)))$/
  if (regPos.test(val) || regNeg.test(val)) {
    return true
  } else {
    return false
  }
}

export const formatDate = (date, pattern) => {
  return dayjs(date).format(pattern)
}

export const showAlert = (message, btnLabel, method) => {
  Modal.alert('', message, [{ text: btnLabel, onPress: method }])
  // fix wechat button drifting issue
  setTimeout(() => {
    window.scrollBy(0, 1)
    window.scrollBy(0, -1)
  }, 800)
}

export const getParameterByName = (name, url = window.location.href) => {
  const normalizedName = name.replace(/[[\]]/g, '\\$&')
  const regex = new RegExp('[?&]' + normalizedName + '(=([^&]*)|&|$)', 'i')
  const results = regex.exec(url)
  if (!results) return null
  if (!results[2]) return ''
  return decodeURIComponent(results[2])
  // return decodeURIComponent(results[2].replace(/\+/g, ' '));
}

export const showModal = (title, content, onOk, onCancel, okText, cancelText) => {
  Modal.alert(title, content, [
    { text: cancelText, onPress: onCancel },
    { text: okText, onPress: onOk },
  ])
}

export const getStoreFullAddress = ({
  addressDTO,
  storeAddressLine1,
  storeAddressLine2,
  storeAddressLine3,
  storeState,
  storeCountry,
}) => {
  if (addressDTO) {
    return addressDTO.addressLine1
      ? `${addressDTO.addressLine1} ${addressDTO.addressLine2} ${addressDTO.addressLine3}
    ${addressDTO.stateName} ${addressDTO.countryName}`
      : ''
  }
  return storeAddressLine1
    ? `${storeAddressLine1} ${storeAddressLine2} ${storeAddressLine3}
    ${storeState} ${storeCountry}`
    : ''
}

export const specialty = [
  {
    name: '适性技术',
    sp: 4568,
    spi: 11783,
    spt: 11784,
    star: true,
    desc: {
      en: 'Upload formal training and/or history of experience.',
      sc: '上传关于培训记录的书面证明文件或训练经验历史的书面证明文件。',
      tc: '上傳關於培訓記錄的書面證明文件或訓練經驗歷史的書面證明文件。',
    },
  },
  {
    name: 'AWARE 鲨鱼保育',
    sp: 3404,
    spi: 9871,
    spt: 9872,
  },
  {
    name: '船潜潜水员',
    sp: 24,
    spi: 54,
    spt: 91,
  },
  {
    name: '深潜潜水员',
    sp: 35,
    spi: 59,
    spt: 93,
  },
  {
    name: '延迟水面浮标（DSMB）潜水员',
    sp: 4251,
    spi: 11541,
    spt: 11542,
  },
  {
    name: '数码水底摄影师',
    sp: 45,
    spi: 64,
    spt: 97,
  },
  {
    name: '打击海洋垃圾',
    sp: 3979,
    spi: 11008,
    spt: 11009,
  },
  {
    name: '潜水员推进器',
    sp: 10,
    spi: 60,
    spt: 94,
  },
  {
    name: '放流潜水员',
    sp: 6,
    spi: 61,
    spt: 95,
  },
  {
    name: '紧急供氧专长',
    sp: 28,
    spi: 6094,
    spt: 6095,
    star: true,
    desc: {
      en: 'Upload emergency oxygen provider certification from other organization.',
      sc: '上传其他组织符合资格的等同级别证书。',
      tc: '上傳其他組織符合資格的等同級別證書。',
    },
  },
  {
    name: '高氧空气潜水员',
    sp: 71,
    spi: 65,
    spt: 98,
    star: true,
    desc: {
      en: 'Upload documentation of a qualifying certification from another training organization for enriched air diver.',
      sc: '上传其他组织符合资格的高氧空气潜水员等同级别证书。',
      tc: '上傳其他組織符合資格的高氧空氣潛水員等同級別證書。',
    },
  },
  {
    name: '鱼类辨识',
    sp: 36,
    spi: 67,
    spt: 100,
  },
  {
    name: '全面镜潜水员',
    sp: 4253,
    spi: 11543,
    spt: 11544,
  },
  {
    name: '水底自然观察家',
    sp: 2,
    spi: 73,
    spt: 105,
  },
  {
    name: '水底导航',
    sp: 77,
    spi: 74,
    spt: 106,
  },
  {
    name: '夜潜潜水员',
    sp: 9,
    spi: 75,
    spt: 107,
  },
  {
    name: '独行侠潜水员',
    sp: 4255,
    spi: 11545,
    spt: 11546,
    star: true,
    desc: {
      en: 'Upload documentation of a qualifying certification from another training organization for self-reliant or technical diver.',
      sc: '上传其他组织符合资格的独行侠潜水员或技术潜水员等同级别证书。',
      tc: '上傳其他組織符合資格的獨行俠潛水員或技術潛水員等同級別證書。',
    },
  },
  {
    name: '水底摄影师',
    sp: 65,
    spi: 82,
    spt: 114,
  },
  {
    name: '沉船潜水员',
    sp: 8,
    spi: 83,
    spt: 115,
  },
  {
    name: '高海拔潜水员',
    sp: 12,
    spi: 52,
    spt: 89,
  },
  {
    name: '干式潜水衣潜水员',
    sp: 34,
    spi: 62,
    spt: 96,
  },
  {
    name: '搜索和寻回潜水员',
    sp: 41,
    spi: 79,
    spt: 111,
  },
  {
    name: '装备专家',
    sp: 52,
    spi: 66,
    spt: 99,
    star: true,
    desc: {
      en: 'Upload documentation of attendance at an equipment manufacturer’s repair clinic or written equipment repair authorization from a manufacturer.',
      sc: '上传参加过装备厂商维修班或装备厂商的装备维修授权书。',
      tc: '上傳參加過裝備廠商維修班或裝備廠商的裝備維修授權書。',
    },
  },
  {
    name: '冰潜潜水员',
    sp: 78,
    spi: 70,
    spt: 103,
    star: true,
    desc: {
      en: 'Upload documentation of a qualifying certification from another training organization for ice diver.',
      sc: '上传其他组织符合资格的冰潜潜水员等同级别证书。',
      tc: '上傳其他組織符合資格的冰潛潛水員等同級別證書。',
    },
  },
  {
    name: '洞窟潜水员',
    sp: 82,
    spi: 55,
    spt: 92,
    star: true,
    desc: {
      en: 'Upload documentation of certification as a full Cave Diver by a recognized Cave certification agency.',
      sc: '上传合格的洞穴潜水认证机构的全洞穴潜水员(Full Cave Diver)证书。',
      tc: '上傳合格的洞穴潛水認證機構的全洞穴潛水員(Full Cave Diver)證書。',
    },
  },
  {
    name: '侧挂气瓶潜水员',
    sp: 3461,
    spi: 9967,
    spt: 9968,
    star: true,
    desc: {
      en: 'Upload documentation of a qualifying certification from another training organization for sidemount diver; or attached documentation of proof of at least 50 sidemount dives completed.',
      sc: '上传其他组织符合资格的侧挂气瓶潜水员等同级别证书; 或附上已经完成 50 次侧挂气瓶潜水的证明文件。',
      tc: '上傳其他組織符合資格的側掛氣瓶潛水員等同級別證書; 或附上已經完成 50 次側掛氣瓶潛水的證明文件。',
    },
  },
]

export const specialtyCd = [
  {
    name: '适性技术',
    sp: 4568,
    spi: 11783,
    spt: 11784,
  },
  {
    name: 'AWARE 鲨鱼保育',
    sp: 3404,
    spi: 9871,
    spt: 9872,
  },
  {
    name: '船潜潜水员',
    sp: 24,
    spi: 54,
    spt: 91,
  },
  {
    name: '深潜潜水员',
    sp: 35,
    spi: 59,
    spt: 93,
  },
  {
    name: '延迟水面浮标（DSMB）潜水员',
    sp: 4251,
    spi: 11541,
    spt: 11542,
  },
  {
    name: '数码水底摄影师',
    sp: 45,
    spi: 64,
    spt: 97,
  },
  {
    name: '打击海洋垃圾',
    sp: 3979,
    spi: 11008,
    spt: 11009,
  },
  {
    name: '潜水员推进器',
    sp: 10,
    spi: 60,
    spt: 94,
  },
  {
    name: '放流潜水员',
    sp: 6,
    spi: 61,
    spt: 95,
  },
  {
    name: '紧急供氧专长',
    sp: 28,
    spi: 6094,
    spt: 6095,
  },
  {
    name: '高氧空气潜水员',
    sp: 71,
    spi: 65,
    spt: 98,
    star: true,
    desc: {
      en: 'Upload documentation of a qualifying certification from another training organization for enriched air diver.',
      sc: '上传其他组织符合资格的高氧空气潜水员等同级别证书。',
      tc: '上傳其他組織符合資格的高氧空氣潛水員等同級別證書。',
    },
  },
  {
    name: '鱼类辨识',
    sp: 36,
    spi: 67,
    spt: 100,
  },
  {
    name: '全面镜潜水员',
    sp: 4253,
    spi: 11543,
    spt: 11544,
  },
  {
    name: '水底自然观察家',
    sp: 2,
    spi: 73,
    spt: 105,
  },
  {
    name: '水底导航',
    sp: 77,
    spi: 74,
    spt: 106,
  },
  {
    name: '夜潜潜水员',
    sp: 9,
    spi: 75,
    spt: 107,
  },
  {
    name: '独行侠潜水员',
    sp: 4255,
    spi: 11545,
    spt: 11546,
    star: true,
    desc: {
      en: 'Upload documentation of a qualifying certification from another training organization for self-reliant or technical diver.',
      sc: '上传其他组织符合资格的独行侠潜水员或技术潜水员等同级别证书。',
      tc: '上傳其他組織符合資格的獨行俠潛水員或技術潛水員等同級別證書。',
    },
  },
  {
    name: '水底摄影师',
    sp: 65,
    spi: 82,
    spt: 114,
  },
  {
    name: '沉船潜水员',
    sp: 8,
    spi: 83,
    spt: 115,
  },
  {
    name: '高海拔潜水员',
    sp: 12,
    spi: 52,
    spt: 89,
  },
  {
    name: '干式潜水衣潜水员',
    sp: 34,
    spi: 62,
    spt: 96,
  },
  {
    name: '搜索和寻回潜水员',
    sp: 41,
    spi: 79,
    spt: 111,
  },
  {
    name: '装备专家',
    sp: 52,
    spi: 66,
    spt: 99,
  },
  {
    name: '冰潜潜水员',
    sp: 78,
    spi: 70,
    spt: 103,
  },
  {
    name: '洞窟潜水员',
    sp: 82,
    spi: 55,
    spt: 92,
    star: true,
    desc: {
      en: 'Upload documentation of certification as a full Cave Diver by a recognized Cave certification agency.',
      sc: '上传合格的洞穴潜水认证机构的全洞穴潜水员(Full Cave Diver)证书。',
      tc: '上傳合格的洞穴潛水認證機構的全洞穴潛水員(Full Cave Diver)證書。',
    },
  },
  {
    name: '侧挂气瓶潜水员',
    sp: 3461,
    spi: 9967,
    spt: 9968,
  },
]

export const specialtyMapping = {
  11783: {
    sc: '适性技术专长教练',
    tc: '適性技術專長教練',
    en: 'PADI Adaptive Techniques Specialty Instructor',
  },
  9871: {
    sc: 'AWARE 鲨鱼保育专长教练',
    tc: 'AWARE 鯊魚保育專長教練',
    en: 'AWARE Shark Conservation Specialty Instructor',
  },
  54: {
    sc: '船潜潜水专长教练',
    tc: '船潛潛水專長教練',
    en: 'Boat Specialty Instructor',
  },
  59: {
    sc: '深潜潜水专长教练',
    tc: '深潛潛水專長教練',
    en: 'Deep Specialty Instructor',
  },
  11541: {
    sc: '延迟水面浮标（DSMB）潜水专长教练',
    tc: '延遲水面浮標（DSMB）潛水專長教練',
    en: 'DELAYED SURFACE MARKER BUOY Specialty Instructor',
  },
  64: {
    sc: '数码水底摄影师专长教练',
    tc: '數碼水底攝影師專長教練',
    en: 'Digital Underwater Photography Instructor',
  },
  11008: {
    sc: '打击海洋垃圾专长教练',
    tc: '打擊海洋垃圾專長教練',
    en: 'Dive Against Debris Specialty Instructor',
  },
  60: {
    sc: '潜水员推进器专长教练',
    tc: '潛水員推進器專長教練',
    en: 'Diver Propulsion Vehicle Specialty Instructor',
  },
  61: {
    sc: '放流潜水专长教练',
    tc: '放流潛水專長教練',
    en: 'Drift Specialty Instructor',
  },
  6094: {
    sc: '紧急供氧专长教练',
    tc: '緊急供氧專長教練',
    en: 'Emergency Oxygen Provider Specialty Instructor',
  },
  65: {
    sc: '高氧空气潜水专长教练',
    tc: '高氧空氣潛水專長教練',
    en: 'Enriched Air Specialty Instructor',
  },
  67: {
    sc: '鱼类辨识专长教练',
    tc: '魚類辨識專長教練',
    en: 'Fish Identification Specialty Instructor',
  },
  11543: {
    sc: '全面镜潜水专长教练',
    tc: '全面鏡潛水專長教練',
    en: 'Full Face Mask Specialty Instructor',
  },
  73: {
    sc: '水底自然观察家专长教练',
    tc: '水底自然觀察家專長教練',
    en: 'Underwater Naturalist Specialty Instructor',
  },
  74: {
    sc: '水底导航专长教练',
    tc: '水底導航專長教練',
    en: 'Underwater Navigator Specialty Instructor',
  },
  75: {
    sc: '夜潜潜水专长教练',
    tc: '夜潛潛水專長教練',
    en: 'Night Specialty Instructor',
  },
  11545: {
    sc: '独行侠潜水专长教练',
    tc: '獨行俠潛水專長教練',
    en: 'Self-Reliant Specialty Instructor',
  },
  82: {
    sc: '水底摄影师专长教练',
    tc: '水底攝影師專長教練',
    en: 'Underwater Videographer Instructor',
  },
  83: {
    sc: '沉船潜水专长教练',
    tc: '沉船潛水專長教練',
    en: 'Wreck Specialty Instructor',
  },
  52: {
    sc: '高海拔潜水专长教练',
    tc: '高海拔潛水專長教練',
    en: 'Altitude Specialty Instructor',
  },
  62: {
    sc: '干式潜水衣潜水专长教练',
    tc: '乾式潛水衣潛水專長教練',
    en: 'Dry Suit Specialty Instructor',
  },
  79: {
    sc: '搜索和寻回潜水专长教练',
    tc: '搜索和尋回潛水專長教練',
    en: 'Search & Recovery Specialty Instructor',
  },
  66: {
    sc: '装备专家专长教练',
    tc: '裝備專家專長教練',
    en: 'Equipment Specialist Specialty Instructor',
  },
  70: {
    sc: '冰潜潜水专长教练',
    tc: '冰潛潛水專長教練',
    en: 'Ice Specialty Instructor',
  },
  55: {
    sc: '洞窟潜水专长教练',
    tc: '洞窟潛水專長教練',
    en: 'Cavern Specialty Instructor',
  },
  9967: {
    sc: '侧挂气瓶潜水专长教练',
    tc: '側掛氣瓶潛水專長教練',
    en: 'Sidemount Specialty Instructor',
  },
}

export const siAppStatus = {
  SI_1: {
    sc: {
      WAIT_ADMIN_APPROVED: '待审核',
      ADMIN_NOT_APPROVED: '审核未通过',
      SUCCESS: '已录入',
      ADMIN_APPROVED: '等待发证',
      SUBMIT: '待审核',
      CD_CREATED: 'CD已提交申请',
      SHIPPED: '等待发证',
      WAIT_PAY: '等待付款',
      PAY_ERROR: '支付异常',
    },
    tc: {
      WAIT_ADMIN_APPROVED: '待審核',
      ADMIN_NOT_APPROVED: '審核未通過',
      SUCCESS: '已錄入',
      ADMIN_APPROVED: '等待發證',
      SUBMIT: '待審核',
      CD_CREATED: 'CD已提交申請',
      SHIPPED: '等待發證',
      WAIT_PAY: '等待付款',
      PAY_ERROR: '支付異常',
    },
    en: {
      WAIT_ADMIN_APPROVED: 'To Confirm',
      ADMIN_NOT_APPROVED: 'Rejected',
      SUCCESS: 'Completed',
      ADMIN_APPROVED: 'Approved',
      SUBMIT: 'To Confirm',
      CD_CREATED: 'Created by CD',
      SHIPPED: 'Wait for Cert',
      WAIT_PAY: 'to Pay',
      PAY_ERROR: 'Pay Error',
    },
  },
  SI_2: {
    sc: {
      WAIT_ADMIN_APPROVED: '待审核',
      ADMIN_NOT_APPROVED: '审核未通过',
      SUCCESS: '已录入',
      ADMIN_APPROVED: '等待发证',
      SUBMIT: '待审核',
      CD_CREATED: 'CD已提交申请',
      SHIPPED: '等待发证',
      WAIT_PAY: '等待付款',
      PAY_ERROR: '支付异常',
    },
    tc: {
      WAIT_ADMIN_APPROVED: '待審核',
      ADMIN_NOT_APPROVED: '審核未通過',
      SUCCESS: '已錄入',
      ADMIN_APPROVED: '等待發證',
      SUBMIT: '待審核',
      CD_CREATED: 'CD已提交申請',
      SHIPPED: '等待發證',
      WAIT_PAY: '等待付款',
      PAY_ERROR: '支付異常',
    },
    en: {
      WAIT_ADMIN_APPROVED: 'To Confirm',
      ADMIN_NOT_APPROVED: 'Rejected',
      SUCCESS: 'Completed',
      ADMIN_APPROVED: 'Approved',
      SUBMIT: 'To Confirm',
      CD_CREATED: 'Created by CD',
      SHIPPED: 'Wait for Cert',
      WAIT_PAY: 'to Pay',
      PAY_ERROR: 'Pay Error',
    },
  },
}

export const cdSiAppStatus = {
  sc: {
    WAIT_ADMIN_APPROVED: '待审核',
    ADMIN_NOT_APPROVED: '审核未通过',
    SUCCESS: '已录入',
    ADMIN_APPROVED: '等待发证',
    SUBMIT: '待审核',
    CD_CREATED: '等待会员签名确认',
    SHIPPED: '等待发证',
    WAIT_PAY: '等待付款',
    PAY_ERROR: '支付异常',
  },
  tc: {
    WAIT_ADMIN_APPROVED: '待審核',
    ADMIN_NOT_APPROVED: '審核未通過',
    SUCCESS: '已錄入',
    ADMIN_APPROVED: '等待發證',
    SUBMIT: '待審核',
    CD_CREATED: '等待會員簽名確認',
    SHIPPED: '等待發證',
    WAIT_PAY: '等待付款',
    PAY_ERROR: '支付異常',
  },
  en: {
    WAIT_ADMIN_APPROVED: 'To Confirm',
    ADMIN_NOT_APPROVED: 'Rejected',
    SUCCESS: 'Completed',
    ADMIN_APPROVED: 'Approved',
    SUBMIT: 'To Confirm',
    CD_CREATED: 'Applicant to Sign',
    SHIPPED: 'Wait for Cert',
    WAIT_PAY: 'to Pay',
    PAY_ERROR: 'Pay Error',
  },
}

export const dataURLtoBlobOrBlodURL = (dataurl, isBlodUrl = false) => {
  let arr = dataurl.split(',')
  let mime = arr[0].match(/:(.*?);/)[1]
  let bstr = atob(arr[1])
  let n = bstr.length
  let u8arr = new Uint8Array(n)
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }
  if (isBlodUrl) {
    return URL.createObjectURL(new Blob([u8arr], { type: mime }))
  }
  return new Blob([u8arr], { type: mime })
}

export const verifyCredits = (credits) => {
  if (document.location.origin === config.URL_prod || document.location.origin === config.URL_prod_tw) {
    return credits < 25
  } else {
    return credits < 1
  }
}

export const getAge = (birthDay) => {
  const today = dayjs()
  const birthDate = dayjs(birthDay)
  const baseAge = Math.abs(today.year()) - Math.abs(birthDate.year())
  const thisBirthday = dayjs(`${today.year()}-${birthDate.month() + 1}-${birthDate.date()}`)
  return today.isBefore(thisBirthday) ? baseAge - 1 : baseAge
}

export const removeExpires = (list) => {
  let newAttachList = list.map((i) =>
    i
      .split('?Expires=')[0]
      .replace('https://files.padi.com.cn/', '')
      .replace('https://assets.padi.com.cn/', '')
      .replace('https://ecards-h5.oss-cn-beijing.aliyuncs.com/', '')
  )
  return newAttachList
}

const regionsMapping = {
  sc: [
    {
      label: '大陆',
      value: 51,
    },
    {
      label: '台湾',
      value: 215,
    },
    {
      label: '香港',
      value: 100,
    },
    {
      label: '澳门',
      value: 130,
    },
  ],
  tc: [
    {
      label: '大陸',
      value: 51,
    },
    {
      label: '台灣',
      value: 215,
    },
    {
      label: '香港',
      value: 100,
    },
    {
      label: '澳門',
      value: 130,
    },
  ],
  en: [
    {
      label: 'Mainland',
      value: 51,
    },
    {
      label: 'Taiwan',
      value: 215,
    },
    {
      label: 'Hong Kong',
      value: 100,
    },
    {
      label: 'Macao',
      value: 130,
    },
  ],
}

export const languagesMapping = [
  {
    label: '简体中文',
    value: 45,
  },
  {
    label: '繁體中文',
    value: 18,
  },
  {
    label: 'English',
    value: 5,
  },
]

export const filterRegion = (region) => {
  const filterRegion = regionsMapping['sc'].find((item) => item.value === region)
  return filterRegion ? filterRegion.label : ''
}

export const filterLanguage = (language) => {
  const filterLang = languagesMapping.find((item) => item.value === language)
  return filterLang ? filterLang.label : ''
}

export const getSubmitOssFormat = (url) => {
  return url.split('?Expires=')[0].replace('https://files.padi.com.cn/', '')
}

const EFR_VALID_MONTH = 24
const efrExpired = (efr) => dayjs(efr.issueDate).add(EFR_VALID_MONTH, 'month').isBefore(dayjs())
export const findEfrOrEfri = (certificates, credentials) =>
  (credentials.find((item) => item.credentialNumber === 35 || item.credentialNumber === 38) && 'EFRI') ||
  certificates.find(
    (item) =>
      (item.certificationNumber === 76 ||
        item.certificationNumber === 68 ||
        item.certificationNumber === 31 ||
        item.certificationNumber === 20) &&
      !efrExpired(item)
  )?.studentNumber ||
  ''
export function IsNotPc() {
  let userAgent = navigator.userAgent,
    Agents = ['Android', 'iPhone', 'SymbianOS', 'Windows Phone', 'iPad', 'iPod']
  return Agents.some((i) => {
    return userAgent.includes(i)
  })
}

export const findCity = (id) => {
  return areaDataSc.find((item) => item.value === id).label
}

export const specialtyInsMapping = {
  11783: '适性技术专长教练',
  9871: 'AWARE 鲨鱼保育专长教练',
  54: '船潜潜水专长教练',
  59: '深潜潜水专长教练',
  11541: '延迟水面浮标（DSMB）潜水专长教练',
  64: '数码水底摄影师专长教练',
  11008: '打击海洋垃圾专长教练',
  60: '潜水员推进器专长教练',
  61: '放流潜水专长教练',
  6094: '紧急供氧专长教练',
  65: '高氧空气潜水专长教练',
  67: '鱼类辨识专长教练',
  11543: '全面镜潜水专长教练',
  73: '水底自然观察家专长教练',
  74: '水底导航专长教练',
  75: '夜潜潜水专长教练',
  11545: '独行侠潜水专长教练',
  82: '水底摄影师专长教练',
  83: '沉船潜水专长教练',
  52: '高海拔潜水专长教练',
  62: '干式潜水衣潜水专长教练',
  79: '搜索和寻回潜水专长教练',
  66: '装备专家专长教练',
  70: '冰潜潜水专长教练',
  55: '洞窟潜水专长教练',
  9967: '侧挂气瓶潜水专长教练',
}

// 大陆地址类型 id：与后端 textbooks_express_type 表新增的大陆行 id 约定一致（CHNA-2618 富友迁移）
export const MAINLAND_ADDRESS_TYPE = 6

export const textbooksRegions = {
  en: [
    {
      label: 'China Mainland',
      value: MAINLAND_ADDRESS_TYPE,
    },
    {
      label: 'Taiwan - Main Island & Remote Area',
      value: 1,
    },
    {
      label: 'Taiwan - Liuqiu Township and Hengchun Township',
      value: 2,
    },
    {
      label: 'Taiwan - Green Island',
      value: 3,
    },
    {
      label: 'Hong Kong',
      value: 4,
    },
    {
      label: 'Macau',
      value: 5,
    },
  ],
  sc: [
    {
      label: '中国大陆',
      value: MAINLAND_ADDRESS_TYPE,
    },
    {
      label: '台湾 - 主岛及偏远地区',
      value: 1,
    },
    {
      label: '台湾 - 琉球及恒春',
      value: 2,
    },
    {
      label: '台湾 - 绿岛',
      value: 3,
    },
    {
      label: '香港',
      value: 4,
    },
    {
      label: '澳门',
      value: 5,
    },
  ],
  tc: [
    {
      label: '中國大陸',
      value: MAINLAND_ADDRESS_TYPE,
    },
    {
      label: '台灣 - 主島及偏遠地區',
      value: 1,
    },
    {
      label: '台灣 - 琉球及恆春',
      value: 2,
    },
    {
      label: '台灣 - 綠島',
      value: 3,
    },
    {
      label: '香港',
      value: 4,
    },
    {
      label: '澳門',
      value: 5,
    },
  ],
}

export const textbooksOrderStatus = {
  sc: [
    {
      code: 'WAIT_PAY',
      status: '待付款',
    },
    {
      code: 'PAID',
      status: '已付款',
    },
    {
      code: 'SHIPPED',
      status: '已发货',
    },
    {
      code: 'CLOSED',
      status: '已过期',
    },
    {
      code: 'REFUNDED',
      status: '已退款',
    },
  ],
  tc: [
    {
      code: 'WAIT_PAY',
      status: '待付款',
    },
    {
      code: 'PAID',
      status: '已付款',
    },
    {
      code: 'SHIPPED',
      status: '已发货',
    },
    {
      code: 'CLOSED',
      status: '已過期',
    },
    {
      code: 'REFUNDED',
      status: '已退款',
    },
  ],
  en: [
    {
      code: 'WAIT_PAY',
      status: 'Waiting for payment',
    },
    {
      code: 'PAID',
      status: 'Already paid',
    },
    {
      code: 'SHIPPED',
      status: 'Shipped',
    },
    {
      code: 'CLOSED',
      status: 'Expired',
    },
    {
      code: 'REFUNDED',
      status: 'Refunded',
    },
  ],
}

export const getCertIdsInCart = (ecards, cart, styleName, isPlastic) => {
  if (!ecards || ecards.length === 0) return []
  const targetType = isPlastic ? '实体卡' : '电子卡'
  // 双形态兼容（购物车响应契约改造 2026-09-02）：slim 条目 type 枚举映射中文标签，
  // rich 条目（BuyCards 本地）typeName 直取
  const typeLabel = (type) => ({ STANDARD_PHYSICAL: '实体卡', ELECTRONIC: '电子卡' })[type] || type
  return cart
    .filter(
      (item) => (item.styleName || item.title) === styleName && (item.typeName || typeLabel(item.type)) === targetType
    )
    .map((item) => item.ecardId)
}

export const IeExamModule = [
  { label: '首次考试', value: 1 },
  { label: '第二次考试', value: 2 },
  { label: '第三次考试', value: 3 },
  { label: '第四次考试', value: 4 },
]

export const IeResitSubjects = [
  { label: '理论考试', value: 1 },
  { label: '知识发展', value: 2 },
  { label: '平静水域', value: 3 },
  { label: '开放水域', value: 4 },
]

export const strToBool = (str) => {
  if (str == null) return false
  const trimmedStr = str.trim().toLowerCase()
  const trueValues = ['true', '1', 'yes', 'y']
  const falseValues = ['false', '0', 'no', 'n']

  if (trueValues.includes(trimmedStr)) return true
  if (falseValues.includes(trimmedStr)) return false
  return false
}
